/**
 * Tools panel sidebar showing connected MCP tools.
 *
 * Displays tools grouped by endpoint (cxs, cxs-service).
 * Updated from stream events via CxsMcpContext.
 * Styled to match DebugSidebar / FilesSidebar pattern.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Wrench, Plug } from 'lucide-react';
import type { ConnectedTool } from '../../context/CxsMcpContext';

interface ToolsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tools: ConnectedTool[];
}

export function ToolsPanel({ isOpen, onClose, tools }: ToolsPanelProps) {
  const [width, setWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Group tools by their group field
  const grouped = tools.reduce<Record<string, ConnectedTool[]>>((acc, tool) => {
    if (!acc[tool.group]) {
      acc[tool.group] = [];
    }
    acc[tool.group].push(tool);
    return acc;
  }, {});

  const groupLabels: Record<string, string> = {
    cxs: 'CXS Platform',
    'cxs-service': 'CXS Service',
  };

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setWidth(Math.max(240, Math.min(600, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (!isOpen) return null;

  return (
    <div
      ref={sidebarRef}
      className="flex flex-col h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 relative"
      style={{ width: `${width}px` }}
      data-testid="tools-panel"
    >
      {/* Resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-violet-400 transition-colors z-10"
        onMouseDown={handleMouseDown}
        style={{ backgroundColor: isResizing ? '#a78bfa' : 'transparent' }}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Wrench size={18} className="text-violet-600 dark:text-violet-400" />
          <span className="font-medium text-gray-900 dark:text-white">MCP Tools</span>
          {tools.length > 0 && (
            <span className="px-2 py-0.5 text-xs bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-full font-medium">
              {tools.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
          title="Close sidebar"
        >
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 bg-gray-50/50 dark:bg-gray-800/20">
        {tools.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Plug className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No MCP tools connected</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Set CXS MCP settings and send a message.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([group, groupTools]) => (
              <div key={group}>
                <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 px-1">
                  {groupLabels[group] || group}
                </h4>
                <div className="space-y-1">
                  {groupTools.map((tool) => (
                    <div
                      key={`${group}-${tool.name}`}
                      className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 hover:border-violet-200 dark:hover:border-violet-700 hover:bg-violet-50/30 dark:hover:bg-violet-900/10 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                        {tool.name}
                      </div>
                      {tool.description && (
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-2">
                          {tool.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
